/* ==========================================================================
   Three Tails — MOTOR DE MOVIMIENTO
   Fuente de verdad: reference/SISTEMA-TIPOGRAFIA-MOVIMIENTO-v1.md (fundador,
   2026-08-06). El aspecto vive en assets/tt-motion.css; aquí sólo vive el
   cuándo.

   «El movimiento es artesanía invisible. Si el usuario nota las animaciones,
    probablemente son demasiado fuertes.»

   ── LA DECISIÓN DE DISEÑO QUE EXPLICA TODO EL ARCHIVO ─────────────────────
   El encargo pedía que «una sección nueva herede la coreografía sin que nadie
   se acuerde de apuntarla». Un sistema donde cada sección declara sus pasos
   se pierde en dos semanas — ya pasó con el ritmo vertical, que era opcional.

   Así que aquí NO hay lista de secciones, ni clases que haya que escribir en
   el Liquid, ni atributos obligatorios. El motor recorre cada sección, deduce
   el papel de cada pieza a partir del marcado que ya existe (una etiqueta
   <h2> es un titular; una clase que contiene «eyebrow» es un antetítulo; un
   <img> es una fotografía) y las ordena SIEMPRE igual:

        antetítulo → titular → párrafo → llamada → fotografía

   La tipografía va delante. Las imágenes llegan las últimas. Sin excepciones
   y sin que nadie tenga que acordarse.

   ── ÍNDICE ────────────────────────────────────────────────────────────────
     1 · Reglas y utilidades
     2 · Clasificador de papeles
     3 · Recolector: de una sección a una lista ordenada de pasos
     4 · Carga de la portada (logotipo → navegación → hero → fotografía)
     5 · Revelado por scroll
     6 · Parallax del hero, tope 4%
     7 · Acordeones sin animar alto
     8 · Barras de progreso: `width` → `scaleX`
     9 · Galería con fundido cruzado
    10 · Arranque, editor de temas y desmontaje
   ========================================================================== */
(function () {
  'use strict';

  /* ========================================================================
     1 · REGLAS Y UTILIDADES
     ======================================================================== */

  /* Con movimiento reducido el revelado NO ARRANCA. Es el seguro más
     importante del archivo: si no se pone `html.tt-mo`, la hoja no esconde
     nada y la página se ve entera, quieta y legible. La trampa clásica de los
     sistemas de revelado es esconder con CSS y confiar en que el JavaScript
     lo destape; aquí el CSS sólo esconde bajo una clase que pone el JS. */
  var reduceQuery = (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)')) ||
                    { matches: false };

  var STEP_CAP  = 8;     // índice máximo de escalonado: 8 × 65ms = 520ms
  var MAX_STEPS = 9;     // pasos distintos por sección (0..8)
  var MAX_PIECES = 24;   // más de esto y se recolecta en grueso
  var RESCUE_MS = 2500;  // temporizador de rescate

  var root = document.documentElement;
  var teardowns = [];

  function isVisible(el) {
    if (!el || el.nodeType !== 1 || el.hidden) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
    // Un envoltorio de caja vacía (por ejemplo `.tt-sys-band__media`, que mide
    // 0px de alto porque su imagen va en posición absoluta) no es una pieza:
    // hay que entrar en él, no animarlo.
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  /* Nada de esto se anima nunca: o está fijo en pantalla, o es un
     recubrimiento, o es chrome cuyo aparecer/desaparecer molestaría. */
  var NEVER = [
    '#tt-pop', 'cart-drawer', '.tt-cart-drawer', '.tt-stickybar', '.tt-fab',
    '.tt-sticky-cta', '.tt-whatsapp', '.tt-cookie', '.tt-cookies',
    '.tt-header', '.tt-announce', '.tt-tick', '.tt-ticker',
    'dialog', 'details', 'summary', 'script', 'style', 'noscript', 'template',
    '.shopify-payment-button', '.tt-mo-fade', '[data-tt-no-motion]'
  ].join(',');

  function isForbidden(el) {
    if (el.matches && el.matches(NEVER)) return true;
    var pos = window.getComputedStyle(el).position;
    return pos === 'fixed' || pos === 'sticky';
  }


  /* ========================================================================
     2 · CLASIFICADOR DE PAPELES
     ------------------------------------------------------------------------
     Devuelve 0..4 (antetítulo, titular, párrafo, llamada, fotografía) o -1 si
     el elemento no es una pieza sino un contenedor por el que seguir bajando.

     Los selectores apuntan al marcado que el tema YA tiene (`tt-sys-eyebrow`,
     `tt-sys-lede`, `tt-hi-slot`…) Y a etiquetas HTML normales. Eso segundo es
     lo que hace que una sección futura funcione sin tocar este archivo: basta
     con que use <h2>, <p> y <img>.
     ======================================================================== */
  var ROLE_EYEBROW = 0, ROLE_HEADING = 1, ROLE_TEXT = 2, ROLE_CTA = 3, ROLE_MEDIA = 4;

  var SEL_EYEBROW = '[class*="eyebrow"],[class*="kicker"],[class*="overline"],[class*="__tag"],[class*="__badge"]';
  var SEL_HEADING = 'h1,h2,h3,h4,[class*="__heading"],[class*="__title"],[class*="-h1"],[class*="-h2"],[class*="-d1"],[class*="-d2"],[class*="__quote"]';
  var SEL_TEXT    = 'p,ul,ol,dl,blockquote,table,[class*="lede"],[class*="__text"],[class*="__desc"],[class*="__note"],[class*="__sub"]';
  var SEL_CTA     = 'a[class*="btn"],button,form,[class*="__cta"],[class*="-cta"],[class*="__actions"],[class*="__buttons"]';
  var SEL_MEDIA   = 'img,picture,video,figure,iframe,canvas,model-viewer,' +
                    '[class*="slot"],[class*="__media"],[class*="-media"],[class*="__img"],' +
                    '[class*="__image"],[class*="__photo"],[class*="__frame"],[class*="__visual"],[class*="__illus"]';

  function roleOf(el) {
    // El orden importa: fotografía primero, porque un <figure> con un <p> de
    // pie dentro tiene que contar como fotografía entera, no como dos pasos.
    if (el.matches(SEL_MEDIA)) return ROLE_MEDIA;
    if (el.matches(SEL_EYEBROW)) return ROLE_EYEBROW;
    if (el.matches(SEL_HEADING)) return ROLE_HEADING;
    if (el.matches(SEL_CTA)) return ROLE_CTA;
    if (el.matches(SEL_TEXT)) return ROLE_TEXT;
    return -1;
  }

  /* ¿Es una rejilla de cosas repetidas (tarjetas, baldosas, columnas)?
     Si lo es, se anima ENTERA como una sola pieza. Sin esto, una sección de
     seis tarjetas produciría treinta pasos y el resultado se leería como
     «cosas apareciendo», que es justo lo contrario del encargo. */
  function isRepeater(el) {
    var kids = el.children;
    if (kids.length < 3) return false;
    var first = (kids[0].getAttribute && kids[0].getAttribute('class') || '').trim().split(/\s+/)[0];
    if (!first) return false;
    var same = 0;
    for (var i = 0; i < kids.length; i++) {
      var c = (kids[i].getAttribute && kids[i].getAttribute('class') || '').trim().split(/\s+/)[0];
      if (c === first) same++;
    }
    return same >= 3;
  }


  /* ========================================================================
     3 · RECOLECTOR
     ------------------------------------------------------------------------
     Recorrido en anchura desde la raíz de la sección. Cuando un elemento
     tiene papel se apunta y NO se entra en él (así un titular no se anima por
     fuera y por dentro a la vez). Cuando no lo tiene, se sigue bajando.
     ======================================================================== */
  function collect(sectionRoot, maxDepth) {
    var found = [];
    var queue = [];
    var i;

    for (i = 0; i < sectionRoot.children.length; i++) queue.push([sectionRoot.children[i], 0]);

    while (queue.length) {
      var pair = queue.shift();
      var el = pair[0], depth = pair[1];

      if (el.nodeType !== 1) continue;
      if (isForbidden(el)) continue;

      if (!isVisible(el) || depth >= maxDepth) {
        // Envoltorio vacío: entrar. Tope de profundidad alcanzado: si tiene
        // papel se apunta, y si no, se deja en paz (no se anima).
        if (!isVisible(el)) {
          for (i = 0; i < el.children.length; i++) queue.push([el.children[i], depth + 1]);
          continue;
        }
        var deepRole = roleOf(el);
        if (deepRole !== -1) found.push([deepRole, el]);
        else found.push([isRepeater(el) ? ROLE_TEXT : ROLE_TEXT, el]);
        continue;
      }

      var role = roleOf(el);

      // Rejilla de piezas repetidas → una sola pieza, y va con las
      // fotografías si contiene alguna (las imágenes llegan las últimas).
      if (role === -1 && isRepeater(el)) {
        found.push([el.querySelector('img,picture,video,[class*="slot"]') ? ROLE_MEDIA : ROLE_TEXT, el]);
        continue;
      }

      if (role === -1) {
        for (i = 0; i < el.children.length; i++) queue.push([el.children[i], depth + 1]);
        continue;
      }

      // Una llamada con dos botones dentro se desdobla: el documento pide
      // «llamada principal» y «llamada secundaria» como dos pasos distintos.
      if (role === ROLE_CTA) {
        var actionable = el.querySelectorAll(':scope > a, :scope > button');
        if (actionable.length >= 2) {
          var pushed = 0;
          for (i = 0; i < actionable.length; i++) {
            if (isVisible(actionable[i])) { found.push([ROLE_CTA, actionable[i]]); pushed++; }
          }
          if (pushed) continue;
        }
      }

      found.push([role, el]);
    }

    /* Orden final: por papel y, dentro del mismo papel, por orden real de
       aparición en el documento. Esto es literalmente la frase del documento
       —«las imágenes llegan siempre las últimas; la tipografía va siempre
       delante»— escrita como comparador. */
    found.sort(function (a, b) {
      if (a[0] !== b[0]) return a[0] - b[0];
      var rel = a[1].compareDocumentPosition(b[1]);
      if (rel & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (rel & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    return found.map(function (f) { return f[1]; });
  }

  /* Recolección con marcha atrás: si una sección es tan densa que produce más
     de MAX_PIECES piezas, se vuelve a recolectar en grueso (dos niveles). Más
     vale que una sección cargada respire en cuatro bloques que en treinta. */
  function collectSection(sectionRoot) {
    var pieces = collect(sectionRoot, 7);
    if (pieces.length > MAX_PIECES) pieces = collect(sectionRoot, 2);
    return pieces;
  }

  /* Reparto de turnos. Si hay más piezas que pasos, varias comparten turno:
     así TODAS se animan (ninguna se queda fuera apareciendo antes de tiempo)
     y la secuencia sigue cabiendo en 8×65 + 650 = 1170ms, por debajo del
     techo de 1200 del documento. */
  function stepFor(k, total) {
    if (total <= MAX_STEPS) return Math.min(k, STEP_CAP);
    return Math.min(STEP_CAP, Math.floor((k * MAX_STEPS) / total));
  }

  function arm(el, index) {
    if (!el || el.__ttMo) return;
    el.__ttMo = true;
    el.style.setProperty('--tt-mo-i', index);
    el.classList.add('tt-mo-t');
  }

  /* Suelta la pieza. Al terminar se le quitan clases y variables: el elemento
     vuelve a su estado natural y no deja una capa de composición viva ni un
     `transform` que rompa un `position: fixed` de algo que caiga dentro. */
  function release(el) {
    if (!el || !el.classList || !el.classList.contains('tt-mo-t')) return;
    el.classList.add('tt-mo-in');

    var idx = parseInt(el.style.getPropertyValue('--tt-mo-i'), 10) || 0;
    var done = function () {
      el.classList.remove('tt-mo-t', 'tt-mo-in');
      el.style.removeProperty('--tt-mo-i');
      el.__ttMo = false;
    };
    // `transitionend` puede no llegar (pestaña en segundo plano, elemento
    // sacado del DOM). El temporizador es la red de seguridad.
    var t = setTimeout(done, 1200 + idx * 65 + 200);
    el.addEventListener('transitionend', function h(e) {
      if (e.target !== el || e.propertyName !== 'opacity') return;
      clearTimeout(t);
      el.removeEventListener('transitionend', h);
      done();
    });
  }

  function armAll(pieces) {
    for (var i = 0; i < pieces.length; i++) arm(pieces[i], stepFor(i, pieces.length));
  }
  function releaseAll(pieces) {
    for (var i = 0; i < pieces.length; i++) release(pieces[i]);
  }


  /* ========================================================================
     4 · CARGA DE LA PORTADA
     ------------------------------------------------------------------------
     «En secuencia: logotipo → navegación → titular del hero → párrafo del
      hero → llamada principal → llamada secundaria → fotografía.»

     Sólo en la PRIMERA llegada de la sesión. En las navegaciones siguientes
     manda la transición de página (500ms) y repetir la coreografía entera
     sería ruido: arriba todo aparece ya puesto y sólo se revela lo que va
     entrando al hacer scroll.
     ======================================================================== */
  var BOOT_KEY = 'tt-mo-booted';

  function firstArrival() {
    try { return sessionStorage.getItem(BOOT_KEY) !== '1'; } catch (e) { return true; }
  }
  function markArrived() {
    try { sessionStorage.setItem(BOOT_KEY, '1'); } catch (e) {}
  }

  function bootSequence() {
    var seq = [];
    var header = document.querySelector('.tt-header');
    if (header) {
      var logo = header.querySelector('.tt-header__logo');
      var nav = header.querySelector('.tt-header__nav');
      var actions = header.querySelector('.tt-header__actions');
      if (logo && isVisible(logo)) seq.push(logo);
      // «Navegación»: los enlaces si se ven (escritorio) y, en cualquier caso,
      // el bloque de acciones — que en el teléfono ES la navegación visible.
      if (nav && isVisible(nav)) seq.push(nav);
      if (actions && isVisible(actions)) seq.push(actions);
    }

    // La primera sección con contenido de la plantilla: el hero en la
    // portada, la ficha en el PDP, el encabezado de la colección…
    var firstSection = null;
    var sections = document.querySelectorAll('.shopify-section');
    for (var i = 0; i < sections.length; i++) {
      if (sections[i].classList.contains('shopify-section-group-header-group')) continue;
      if (!isVisible(sections[i])) continue;
      firstSection = sections[i];
      break;
    }
    if (firstSection) {
      var pieces = collectSection(firstSection);
      for (var j = 0; j < pieces.length; j++) seq.push(pieces[j]);
      firstSection.__ttMoDone = true;
    }
    return seq;
  }

  function runBoot() {
    var seq = bootSequence();
    var i;
    for (i = 0; i < seq.length; i++) {
      var heroMedia = seq[i].closest && seq[i].closest('#tt-hero') && seq[i].matches(SEL_MEDIA);
      /* La fotografía del hero: turno 2 y 1000ms. El porqué —y por qué es el
         único sitio donde este sistema se aparta de la letra del documento—
         está escrito entero en assets/tt-motion.css §1, junto a la regla.
         El turno 2 sale de una cuenta, no de un gusto: el último texto de la
         portada es el turno 7, o sea 7×65 + 650 = 1105ms. Para que la foto
         siga siendo lo último en asentarse necesita acabar después de eso, y
         2×65 + 1000 = 1130ms. Total de la carga: 1130ms, por debajo del techo
         de 1200 del documento. */
      arm(seq[i], heroMedia ? 2 : stepFor(i, seq.length));
      if (heroMedia) seq[i].classList.add('tt-mo-t--hero-media');
    }
    // Dos fotogramas: uno para que el navegador aplique el estado escondido y
    // otro para que la transición tenga de dónde salir.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { releaseAll(seq); });
    });
  }


  /* ========================================================================
     5 · REVELADO POR SCROLL
     ------------------------------------------------------------------------
     Se observa LA SECCIÓN, no cada pieza: así el escalonado es por sección
     (que es lo que pide el documento) y hay un observador por sección en vez
     de doscientos por página.
     ======================================================================== */
  var sectionObserver = null;

  function initScrollReveal(scope) {
    if (!('IntersectionObserver' in window)) return;

    if (!sectionObserver) {
      sectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          sectionObserver.unobserve(entry.target);
          releaseAll(entry.target.__ttMoPieces || []);
          entry.target.__ttMoPieces = null;
        });
      }, {
        /* Se dispara cuando la sección asoma un 12% de pantalla. El documento
           pide que el desplazamiento se sienta «como pasar páginas» y que
           «nunca haya zonas muertas»: con el margen negativo, para cuando el
           usuario llega a leerla, la sección ya terminó de aparecer. */
        threshold: 0,
        rootMargin: '0px 0px -12% 0px'
      });
      teardowns.push(function () {
        if (sectionObserver) sectionObserver.disconnect();
        sectionObserver = null;
      });
    }

    var host = scope || document;
    var list = host.querySelectorAll ? host.querySelectorAll('.shopify-section') : [];
    Array.prototype.forEach.call(list, function (section) {
      if (section.classList.contains('shopify-section-group-header-group')) return;
      if (section.__ttMoDone) return;
      /* Si ya se ve al cargar (por encima del pliegue) no se esconde: quitar
         de la vista algo que el usuario ya está leyendo para volver a
         enseñárselo es peor que no animarlo. */
      var r = section.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.9) { section.__ttMoDone = true; return; }

      section.__ttMoDone = true;
      var pieces = collectSection(section);
      section.__ttMoPieces = pieces;
      armAll(pieces);
      sectionObserver.observe(section);
    });
  }

  /* Rescate. Nada puede quedarse invisible: ni por un observador que no
     dispara, ni por una sección que nunca entra del todo, ni por un
     rastreador que no hace scroll. */
  function rescue() {
    var stuck = document.querySelectorAll('.tt-mo-t:not(.tt-mo-in)');
    Array.prototype.forEach.call(stuck, function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 1.5) release(el);
    });
  }


  /* ========================================================================
     6 · PARALLAX DEL HERO
     ------------------------------------------------------------------------
     «Muy sutil. Movimiento máximo 4%. Sólo en la imagen del hero. Casi
      imperceptible.»

     4% del ALTO DEL HERO, no del scroll: un hero de 800px se mueve como mucho
     32px en todo su recorrido. En móvil no se aplica — el documento pide
     reducir distancias allí, y en pantalla táctil el parallax se nota más de
     lo que aporta.
     ======================================================================== */
  function initParallax() {
    var hero = document.getElementById('tt-hero');
    if (!hero) return;
    var img = hero.querySelector('.tt-hi-slot__img');
    if (!img) return;
    if (window.innerWidth < 990) return;
    if (!window.matchMedia || !window.matchMedia('(pointer: fine)').matches) return;

    root.classList.add('tt-mo-parallax');

    var ticking = false;
    var maxPx = 0;

    function measure() { maxPx = hero.getBoundingClientRect().height * 0.04; }

    function frame() {
      ticking = false;
      var r = hero.getBoundingClientRect();
      if (r.bottom <= 0 || r.top >= window.innerHeight) return;
      var progress = Math.max(-1, Math.min(1, -r.top / Math.max(1, r.height)));
      img.style.transform = 'translate3d(0,' + (progress * maxPx).toFixed(2) + 'px,0)';
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    }

    measure();
    frame();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    teardowns.push(function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      root.classList.remove('tt-mo-parallax');
      img.style.transform = '';
    });
  }


  /* ========================================================================
     7 · ACORDEONES
     ------------------------------------------------------------------------
     «Animación de 320ms. El chevron rota con suavidad. El contenido se
      desvanece DESPUÉS de la expansión. Nunca abrir de golpe.»
     Y del apartado de rendimiento: «Nunca animar height».

     Los dos acordeones de preguntas frecuentes del tema ya usaban la técnica
     correcta (`grid-template-rows: 0fr → 1fr`) y sólo había que retocarles
     los tiempos: eso lo hace la hoja. El que no animaba NADA —abría de
     golpe— es el <details> nativo de la sección de beneficios de la ficha.
     Eso se resuelve aquí, sin tocar la sección:

       · el contenido se envuelve en `.tt-mo-acc > .tt-mo-acc__inner`, que es
         donde la hoja hace 0fr → 1fr;
       · al cerrar, el atributo `open` se retiene 320ms para que dé tiempo a
         encogerse, y se retira al terminar;
       · el atributo `name` (acordeón exclusivo del navegador) se retira y la
         exclusividad se reimplementa aquí. Hace falta: la exclusividad nativa
         cierra al hermano AL INSTANTE, sin dejar animarlo, y además volver a
         poner `open` para animar el cierre reabriría al otro.
     ======================================================================== */
  var accGroups = {};

  function upgradeDetails(el) {
    if (el.__ttMoAcc || el.hasAttribute('data-tt-no-motion')) return;
    el.__ttMoAcc = true;
    el.classList.add('tt-mo-details');

    // Exclusividad: se pasa del navegador a este archivo. `data-tt-mo-group`
    // sobrevive a una serialización del HTML; `name` no volvería.
    var group = el.getAttribute('name') || el.getAttribute('data-tt-mo-group');
    if (group) {
      el.removeAttribute('name');
      el.setAttribute('data-tt-mo-group', group);
      var bucket = accGroups[group] = (accGroups[group] || []).filter(function (n) {
        return n.isConnected;   // los del DOM anterior se sueltan al recargar
      });
      bucket.push(el);
    }

    /* ⚠ Reutilizar el envoltorio si ya existe, en vez de crear otro.
       El editor de temas reconstruye el DOM de la sección, y hay más de un
       camino por el que el HTML que vuelve ya trae dentro el `.tt-mo-acc` de
       la vez anterior (cualquier ida y vuelta por `innerHTML` lo hace). Sin
       esta comprobación, cada recarga mete un envoltorio dentro del anterior:
       medido, tres recargas dejaban 16 envoltorios donde había 4, doce de
       ellos anidados, y el acordeón dejaba de abrir. */
    var wrap = el.querySelector(':scope > .tt-mo-acc');
    var inner = wrap && wrap.querySelector(':scope > .tt-mo-acc__inner');
    var fresh = !wrap;

    if (fresh) {
      wrap = document.createElement('div');
      wrap.className = 'tt-mo-acc';
      inner = document.createElement('div');
      inner.className = 'tt-mo-acc__inner';
      wrap.appendChild(inner);
    } else if (!inner) {
      inner = document.createElement('div');
      inner.className = 'tt-mo-acc__inner';
      while (wrap.firstChild) inner.appendChild(wrap.firstChild);
      wrap.appendChild(inner);
    }

    var moved = [];
    var node = el.firstChild;
    while (node) {
      var next = node.nextSibling;
      if (node !== wrap && !(node.nodeType === 1 && node.tagName === 'SUMMARY')) moved.push(node);
      node = next;
    }
    if (fresh && !moved.length) { el.__ttMoAcc = false; return; }
    moved.forEach(function (n) { inner.appendChild(n); });
    if (fresh) el.appendChild(wrap);
    // El envoltorio recuperado puede venir con el estado de la vez anterior.
    wrap.classList.toggle('tt-mo-acc--open', el.open);

    var closeTimer = null;
    var selfWrite = false;

    function paint(open) {
      el.classList.toggle('tt-mo-open', open);
      wrap.classList.toggle('tt-mo-acc--open', open);
    }

    function closeNow() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      selfWrite = true;
      el.open = false;
    }

    function animateClose() {
      paint(false);
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = setTimeout(closeNow, 340);
    }

    function animateOpen() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      requestAnimationFrame(function () { paint(true); });
      // Exclusividad reimplementada: los hermanos del grupo se cierran, pero
      // ANIMADOS. Ésta es la razón entera de haber quitado `name`.
      var g = el.getAttribute('data-tt-mo-group');
      if (!g || !accGroups[g]) return;
      accGroups[g].forEach(function (other) {
        if (other === el || !other.isConnected) return;
        if (other.open && other.__ttMoClose) other.__ttMoClose();
      });
    }

    /* ⚠ Aquí NO se escribe `el.open`. Escribirlo —aunque sea con el mismo
       valor— cuenta como mutación del atributo, el observador de este mismo
       elemento se despierta, ve `open` puesto, lo interpreta como «lo acaban
       de abrir», y cierra al hermano… que hace lo mismo de vuelta. Dos
       acordeones del mismo grupo abriéndose y cerrándose el uno al otro para
       siempre: el hilo principal se queda clavado y la página deja de
       responder al primer clic. Pasó, y así es como se arregla: el cierre
       animado no toca el atributo; sólo pinta. Lo quita `closeNow()` al
       final, y ésa sí es una escritura declarada. */
    el.__ttMoClose = function () {
      if (!el.open) return;
      animateClose();
    };

    if (el.open) paint(true);

    var mo = new MutationObserver(function () {
      if (selfWrite) { selfWrite = false; return; }
      if (el.open) animateOpen();
      else {
        // El usuario cerró: se devuelve `open` para poder animar el cierre.
        selfWrite = true;
        el.open = true;
        animateClose();
      }
    });
    mo.observe(el, { attributes: true, attributeFilter: ['open'] });
    teardowns.push(function () { mo.disconnect(); });
  }

  function initAccordions(scope) {
    var host = scope || document;
    var list = host.querySelectorAll ? host.querySelectorAll('details') : [];
    Array.prototype.forEach.call(list, upgradeDetails);
  }


  /* ========================================================================
     8 · BARRAS DE PROGRESO — de `width` a `scaleX`
     ------------------------------------------------------------------------
     «Nunca animar width.» Cuatro barras del tema lo hacían: la de envío
     gratis del carrito (dos variantes), la del carril de la ficha y la del
     cuestionario. Sus dueños siguen escribiendo lo mismo que escribían —un
     ancho en línea o la variable `--tt-hi-progress`—; aquí se traduce a una
     escala, que sí es compositable. Ni una línea de aquellos archivos cambia.
     ======================================================================== */
  var BAR_SELECTORS = '.tt-fs__fill,.tt-freeship__fill,.tt-hi-rail-progress__bar,.tt-quiz__progress-fill,.tt-quiz__bar-fill';

  function readFraction(el) {
    var v = el.style.getPropertyValue('--tt-hi-progress') || el.style.width;
    if (!v) v = window.getComputedStyle(el).getPropertyValue('--tt-hi-progress');
    v = String(v || '').trim();
    var n = parseFloat(v);
    if (isNaN(n)) return null;
    if (v.slice(-1) === '%') return n / 100;
    var parent = el.parentElement;
    if (!parent) return null;
    var pw = parent.getBoundingClientRect().width;
    return pw > 0 ? n / pw : null;
  }

  function upgradeBar(el) {
    if (el.__ttMoBar) return;
    el.__ttMoBar = true;
    el.classList.add('tt-mo-bar');

    /* ⚠ La escritura de `--tt-mo-bar` toca el atributo `style`, que es
       justo lo que este observador vigila. Sin el candado, cada escritura
       provoca otra lectura y otra escritura: bucle infinito que deja el hilo
       principal clavado. Costó una tanda de pruebas descubrirlo porque la
       página CARGA bien —el bucle es de microtareas— y sólo se nota cuando
       algo intenta hacer clic. */
    var writing = false;
    var last = null;

    function sync() {
      if (writing) { writing = false; return; }
      var f = readFraction(el);
      if (f === null) return;
      var v = Math.max(0, Math.min(1, f)).toFixed(4);
      if (v === last) return;
      last = v;
      writing = true;
      el.style.setProperty('--tt-mo-bar', v);
    }
    sync();

    var mo = new MutationObserver(sync);
    mo.observe(el, { attributes: true, attributeFilter: ['style'] });
    teardowns.push(function () { mo.disconnect(); });
  }

  function initBars(scope) {
    var host = scope || document;
    var list = host.querySelectorAll ? host.querySelectorAll(BAR_SELECTORS) : [];
    Array.prototype.forEach.call(list, upgradeBar);
  }


  /* ========================================================================
     9 · GALERÍA CON FUNDIDO CRUZADO
     ------------------------------------------------------------------------
     «Fundido cruzado entre imágenes. Nunca deslizamiento. Las marcas de lujo
      funden. El comercio masivo desliza.»

     Motor genérico: se activa en cualquier carril cuya raíz lleve
     `data-tt-mode="fade"`. Lo que hace:
       · apila las piezas de la pista en el mismo hueco (lo hace la hoja) y
         funde de una a otra en 650ms;
       · CONSERVA los puntos de paginación, el conteo («3 de 5») y los botones
         anterior/siguiente: reutiliza el contrato `[data-tt-rail-*]` que ya
         existe en el tema en vez de inventar otro;
       · desmonta antes el controlador de deslizamiento
         (assets/tt-pdp-rail.js expone `__ttRailTeardown` justo para esto),
         así no quedan dos controladores peleándose por la misma pista.

     Sin el atributo no se activa nada y el carril sigue siendo el de siempre.
     ======================================================================== */
  function initFadeGalleries(scope) {
    var host = scope || document;
    var roots = host.querySelectorAll ? host.querySelectorAll('[data-tt-mode="fade"]') : [];
    Array.prototype.forEach.call(roots, function (r) {
      if (r.__ttMoFade) return;
      var track = r.querySelector('[data-tt-rail-track]');
      if (!track) return;
      r.__ttMoFade = true;

      if (r.__ttRailTeardown) r.__ttRailTeardown();
      r.__ttRailBound = true;   // y que el otro controlador no vuelva a montar

      track.classList.add('tt-mo-fade');
      if (!track.hasAttribute('tabindex')) track.setAttribute('tabindex', '0');

      var slides = Array.prototype.filter.call(track.children, function (n) { return n.nodeType === 1; });
      if (slides.length < 2) return;

      var dots = Array.prototype.slice.call(r.querySelectorAll('[data-tt-rail-dot]'));
      var live = r.querySelector('[data-tt-rail-live]');
      var prev = r.querySelector('[data-tt-rail-prev]');
      var next = r.querySelector('[data-tt-rail-next]');
      var bar  = r.querySelector('[data-tt-rail-progress]');
      var i = 0;
      var local = [];

      function on(el, type, fn, opts) {
        if (!el) return;
        el.addEventListener(type, fn, opts);
        local.push(function () { el.removeEventListener(type, fn, opts); });
      }

      function show(n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, k) {
          s.classList.toggle('tt-mo-fade-on', k === i);
          s.setAttribute('aria-hidden', k === i ? 'false' : 'true');
        });
        dots.forEach(function (d, k) {
          if (k === i) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
        if (live) live.textContent = (i + 1) + ' de ' + slides.length;
        if (bar) bar.style.setProperty('--tt-mo-bar', ((i + 1) / slides.length).toFixed(4));
        if (prev) prev.disabled = (i === 0);
        if (next) next.disabled = (i === slides.length - 1);
      }

      dots.forEach(function (d, k) { on(d, 'click', function () { show(k); }); });
      on(prev, 'click', function () { show(i - 1); });
      on(next, 'click', function () { show(i + 1); });
      on(track, 'keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); show(i + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); show(i - 1); }
        else if (e.key === 'Home') { e.preventDefault(); show(0); }
        else if (e.key === 'End') { e.preventDefault(); show(slides.length - 1); }
      });

      // El dedo sigue eligiendo, pero lo que se ve es un fundido, no un
      // arrastre: el gesto se conserva, el deslizamiento no.
      var x0 = null;
      on(track, 'touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
      on(track, 'touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 40) show(dx < 0 ? i + 1 : i - 1);
      }, { passive: true });

      show(0);

      r.__ttMoFadeTeardown = function () {
        local.forEach(function (f) { f(); });
        r.__ttMoFade = false;
        r.__ttMoFadeTeardown = null;
      };
      teardowns.push(function () { if (r.__ttMoFadeTeardown) r.__ttMoFadeTeardown(); });
    });
  }


  /* ========================================================================
     10 · ARRANQUE, EDITOR DE TEMAS Y DESMONTAJE
     ------------------------------------------------------------------------
     El editor de temas destruye y reconstruye el DOM de una sección cada vez
     que se edita. Sin desmontar, cada edición dejaría vivos los oyentes
     viejos: tres recargas, tres copias de cada oyente. Todo lo que se
     engancha se apunta en `teardowns`, y cada pieza comprueba su propia marca
     (`__ttMo*`) antes de volver a engancharse.
     ======================================================================== */
  function initEverything(scope) {
    initAccordions(scope);
    initBars(scope);
    initFadeGalleries(scope);
    if (!reduceQuery.matches) initScrollReveal(scope);
  }

  function start() {
    /* Estas tres funcionan SIEMPRE, también con movimiento reducido: un
       acordeón sin animación sigue necesitando su envoltorio, y una barra de
       progreso sigue sin poder animar `width`. */
    initAccordions();
    initBars();
    initFadeGalleries();

    if (reduceQuery.matches) return;   // ← el seguro: no se esconde nada

    root.classList.add('tt-mo');

    if (firstArrival()) { runBoot(); markArrived(); }

    initScrollReveal();
    initParallax();

    setTimeout(rescue, RESCUE_MS);
    window.addEventListener('load', function () { setTimeout(rescue, 600); }, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (e) {
    /* Una sección recién montada en el editor se ve entera desde el primer
       momento: esconderla para revelarla mientras el fundador la edita sería
       exactamente el tipo de animación que se nota. */
    if (e.target) e.target.__ttMoDone = true;
    initEverything(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var t = e.target;
    if (!t || !t.querySelectorAll) return;
    Array.prototype.forEach.call(t.querySelectorAll('[data-tt-mode="fade"]'), function (r) {
      if (r.__ttMoFadeTeardown) r.__ttMoFadeTeardown();
    });
  });

  // Si el usuario cambia la preferencia de movimiento en caliente se le hace
  // caso sin recargar: se suelta todo lo escondido.
  if (reduceQuery.addEventListener) {
    reduceQuery.addEventListener('change', function () {
      if (!reduceQuery.matches) return;
      root.classList.remove('tt-mo');
      Array.prototype.forEach.call(document.querySelectorAll('.tt-mo-t'), function (el) {
        el.classList.remove('tt-mo-t', 'tt-mo-in');
        el.style.removeProperty('--tt-mo-i');
        el.__ttMo = false;
      });
    });
  }

  window.addEventListener('pagehide', function () {
    teardowns.forEach(function (f) { try { f(); } catch (e) {} });
    teardowns.length = 0;
  });
})();
